import { useEffect } from 'react';
import { ApiError, getAccessToken, setAccessToken } from '@/api/client';
import { identityApi } from '@/api/endpoints';

/** How long without using the app before the session ends. Must match the server's `idle-timeout`. */
export const IDLE_TIMEOUT_MS = 10 * 60_000;
/** Default maximum session duration: 60 minutes from initial sign-in. */
export const DEFAULT_MAX_SESSION_MS = 60 * 60_000;
/** Throttle writing activity timestamp to localStorage: at most once every 10 seconds. */
const ACTIVITY_STORAGE_THROTTLE_MS = 10_000;
/** Renew at most this often while the person is active. */
const RENEW_EVERY_MS = 60_000;
/** A failed request gets another chance after real input without waiting for the full renewal interval. */
const RENEW_RETRY_MS = 5_000;
/** Real use of the page. Background refetches deliberately do not count as activity. */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

export interface SessionPolicyOptions {
  idleTimeoutMinutes?: number;
  maxSessionMinutes?: number;
}

function readStoredTimestamp(key: string): number | null {
  try {
    const val = localStorage.getItem(key);
    return val !== null && val !== '' && !isNaN(Number(val)) ? Number(val) : null;
  } catch {
    return null;
  }
}

function readStoredNumber(key: string): number | null {
  try {
    const val = localStorage.getItem(key);
    return val ? Number(val) : null;
  } catch {
    return null;
  }
}

/**
 * Keeps an active session alive and ends an idle or expired one.
 *
 * The server issues tokens that expire after the idle timeout, so an abandoned tab is signed out
 * by the token itself; this renews the token while the person is working and signs them out
 * cleanly - with a reason - instead of letting the next request fail. A deployment without
 * renewal (the development token issuer) answers 404 once and is not asked again; the idle
 * sign-out still applies there.
 *
 * Activity is synchronized across tabs via throttled `dam.lastActivity` in `localStorage`, and
 * a maximum session lifetime (default 12 hours from `dam.sessionStart`) is strictly enforced.
 */
export function useIdleSession(
  active: boolean,
  onIdle: (reason?: 'idle' | 'max_lifetime') => void,
  policy?: SessionPolicyOptions,
) {
  const idleTimeoutMinutes =
    policy?.idleTimeoutMinutes ??
    readStoredNumber('dam.idleTimeoutMinutes') ??
    10;
  const maxSessionMinutes =
    policy?.maxSessionMinutes ??
    readStoredNumber('dam.maxSessionMinutes') ??
    720;

  const idleTimeoutMs = idleTimeoutMinutes * 60_000;
  const maxSessionMs = maxSessionMinutes * 60_000;

  useEffect(() => {
    if (!active) return;
    let lastActivity = Date.now();
    let lastStoredActivityWrite = 0;
    // Renew on the first activity: a token restored after a reload may be nearly expired already.
    let nextRenewalAt = 0;
    let renewing = false;
    let renewable = true;
    let ended = false;
    // Set once this session is over - signed out, idle, or the hook torn down - so a renewal still
    // in flight can never put a token back after sign-out.
    let disposed = false;

    // Ensure sessionStart is tracked in localStorage if missing
    try {
      if (!localStorage.getItem('dam.sessionStart')) {
        localStorage.setItem('dam.sessionStart', String(Date.now()));
      }
    } catch {
      // storage unavailable
    }

    const end = (reason: 'idle' | 'max_lifetime' = 'idle') => {
      if (ended) return;
      ended = true;
      onIdle(reason);
    };

    const writeActivityToStorage = (timestamp: number) => {
      try {
        localStorage.setItem('dam.lastActivity', String(timestamp));
        lastStoredActivityWrite = timestamp;
      } catch {
        // storage unavailable
      }
    };

    const checkExpiration = () => {
      const now = Date.now();
      const sessionStart = readStoredTimestamp('dam.sessionStart');
      if (sessionStart !== null && now - sessionStart >= maxSessionMs) {
        return end('max_lifetime');
      }
      const storedActivity = readStoredTimestamp('dam.lastActivity') ?? 0;
      const effectiveLastActivity = Math.max(lastActivity, storedActivity);
      if (now - effectiveLastActivity >= idleTimeoutMs) {
        return end('idle');
      }
    };

    const markActive = () => {
      const now = Date.now();
      const sessionStart = readStoredTimestamp('dam.sessionStart');
      if (sessionStart !== null && now - sessionStart >= maxSessionMs) {
        return end('max_lifetime');
      }

      const storedActivity = readStoredTimestamp('dam.lastActivity') ?? 0;
      const effectiveLastActivity = Math.max(lastActivity, storedActivity);
      if (now - effectiveLastActivity >= idleTimeoutMs) {
        return end('idle');
      }

      lastActivity = now;
      if (now - lastStoredActivityWrite >= ACTIVITY_STORAGE_THROTTLE_MS) {
        writeActivityToStorage(now);
      }

      if (!renewable || renewing || now < nextRenewalAt) return;
      renewing = true;
      nextRenewalAt = now + RENEW_EVERY_MS;
      const sent = getAccessToken();
      identityApi.renewSession()
        .then((renewed) => {
          if (!disposed && !ended && getAccessToken() === sent) {
            setAccessToken(renewed.token);
            if (renewed.idleTimeoutMinutes) {
              try {
                localStorage.setItem('dam.idleTimeoutMinutes', String(renewed.idleTimeoutMinutes));
              } catch {}
            }
            if (renewed.maxSessionMinutes) {
              try {
                localStorage.setItem('dam.maxSessionMinutes', String(renewed.maxSessionMinutes));
              } catch {}
            }
          }
        })
        .catch((cause: unknown) => {
          if (cause instanceof ApiError && cause.status === 404) renewable = false;
          else nextRenewalAt = Date.now() + RENEW_RETRY_MS;
          // A 401 is handled by the client: the session is already over.
        })
        .finally(() => { renewing = false; });
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'dam.lastActivity' && event.newValue) {
        const val = Number(event.newValue);
        if (val > lastActivity) {
          lastActivity = val;
        }
      }
    };

    const timer = window.setInterval(checkExpiration, 15_000);
    window.addEventListener('storage', onStorage);
    // Capture user input before a widget can stop bubbling at a nested control.
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, markActive, { capture: true, passive: true }));
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('storage', onStorage);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, markActive, true));
    };
  }, [active, onIdle, idleTimeoutMs, maxSessionMs]);
}
